/**
 * AdminReviewsPage — moderate parent-submitted reviews.
 *
 * Endpoints (all admin-gated on the backend):
 *   GET    /api/reviews/?page_size=…            — list (admin sees all)
 *   POST   /api/reviews/<id>/approve/           — flip is_approved=true
 *   POST   /api/reviews/<id>/feature/           — toggle is_featured
 *   POST   /api/reviews/<id>/respond/           — add a public response
 *   DELETE /api/reviews/<id>/                   — remove (spam)
 *
 * Filters: Pending (needs moderation) / Approved / Featured / All.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Loader2, Check, Star, Trash2, MessageSquare, Search,
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminReviewsPage() {
  const { api } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending | approved | featured | all
  const [q, setQ] = useState('');
  const [respondTo, setRespondTo] = useState(null);
  const [respondText, setRespondText] = useState('');
  const [respondSaving, setRespondSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reviews/?page_size=500');
      setReviews(Array.isArray(data) ? data : data.results || []);
    } catch {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = reviews;
    if (filter === 'pending') rows = rows.filter(r => !r.is_approved);
    if (filter === 'approved') rows = rows.filter(r => r.is_approved && !r.is_featured);
    if (filter === 'featured') rows = rows.filter(r => r.is_featured);
    if (q.trim()) {
      const needle = q.toLowerCase();
      rows = rows.filter(r =>
        (r.title || '').toLowerCase().includes(needle)
        || (r.comment || '').toLowerCase().includes(needle)
        || (r.user_name || '').toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [reviews, filter, q]);

  const counts = useMemo(() => ({
    pending: reviews.filter(r => !r.is_approved).length,
    approved: reviews.filter(r => r.is_approved && !r.is_featured).length,
    featured: reviews.filter(r => r.is_featured).length,
    all: reviews.length,
  }), [reviews]);

  const toggle = async (r, endpoint, successText) => {
    try {
      await api.post(`/reviews/${r.id}/${endpoint}/`);
      toast.success(successText);
      load();
    } catch {
      toast.error('Action failed');
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete this review?\n\n"${(r.comment || '').slice(0, 80)}…"`)) return;
    try {
      await api.delete(`/reviews/${r.id}/`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const saveResponse = async () => {
    if (!respondTo) return;
    setRespondSaving(true);
    try {
      await api.post(`/reviews/${respondTo.id}/respond/`, { response: respondText });
      toast.success('Response saved');
      setRespondTo(null);
      setRespondText('');
      load();
    } catch {
      toast.error('Save failed');
    } finally {
      setRespondSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reviews-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Star className="h-6 w-6" /> Reviews
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Approve, feature, respond to, or remove parent-submitted reviews.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="reviews-tab-pending">
              Pending <Badge className="ml-2 bg-amber-500/10 text-amber-700">{counts.pending}</Badge>
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="reviews-tab-approved">
              Approved <Badge className="ml-2">{counts.approved}</Badge>
            </TabsTrigger>
            <TabsTrigger value="featured" data-testid="reviews-tab-featured">
              Featured <Badge className="ml-2 bg-amber-500 text-white">{counts.featured}</Badge>
            </TabsTrigger>
            <TabsTrigger value="all">All <Badge className="ml-2">{counts.all}</Badge></TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative ml-auto">
          <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8 w-64"
            placeholder="Search title / body / author"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">No reviews match this filter.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div
              key={r.id}
              className="border rounded-lg p-4 bg-card"
              data-testid={`review-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-amber-500 text-sm">
                      {'★'.repeat(r.rating || 0)}
                      <span className="text-gray-300">{'★'.repeat(5 - (r.rating || 0))}</span>
                    </span>
                    {r.title && <span className="font-semibold">{r.title}</span>}
                    {!r.is_approved && <Badge className="bg-amber-500/20 text-amber-800">Pending</Badge>}
                    {r.is_featured && <Badge className="bg-amber-500 text-white">Featured</Badge>}
                  </div>
                  <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">{r.comment}</p>
                  {r.response && (
                    <div className="mt-2 p-2 rounded bg-blue-50 text-sm border-l-4 border-blue-400">
                      <span className="font-semibold text-blue-800">Our response:</span> {r.response}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground mt-2">
                    By {r.user_name || 'Anonymous'} · {new Date(r.created_at).toLocaleDateString()}
                    {r.course_title && ` · ${r.course_title}`}
                    {r.camp_name && ` · ${r.camp_name}`}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {!r.is_approved && (
                    <Button size="sm" onClick={() => toggle(r, 'approve', 'Approved')} data-testid={`approve-review-${r.id}`}>
                      <Check className="h-3 w-3 mr-1" /> Approve
                    </Button>
                  )}
                  <Button
                    size="sm" variant={r.is_featured ? 'default' : 'outline'}
                    className={r.is_featured ? 'bg-amber-500 hover:bg-amber-600' : ''}
                    onClick={() => toggle(r, 'feature', r.is_featured ? 'Un-featured' : 'Featured')}
                    data-testid={`feature-review-${r.id}`}
                  >
                    <Star className="h-3 w-3 mr-1" /> {r.is_featured ? 'Un-feature' : 'Feature'}
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => { setRespondTo(r); setRespondText(r.response || ''); }}
                    data-testid={`respond-review-${r.id}`}
                  >
                    <MessageSquare className="h-3 w-3 mr-1" /> Respond
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(r)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!respondTo} onOpenChange={(o) => !o && setRespondTo(null)}>
        <DialogContent data-testid="respond-dialog">
          <DialogHeader>
            <DialogTitle>Public response</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground mb-2">
            Shown publicly beneath the review on the site.
          </p>
          <Label>Response</Label>
          <Textarea
            rows={5}
            value={respondText}
            onChange={e => setRespondText(e.target.value)}
            placeholder="Thanks for the kind words, we'll pass this along to our team!"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondTo(null)}>Cancel</Button>
            <Button onClick={saveResponse} disabled={respondSaving}>
              {respondSaving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Save response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
